// Regelset-Editor: links Stammdaten (Lieferanten-ID/-name) + JSON-Textarea (monospace,
// try/JSON.parse-Fehleranzeige); rechts Vorschau gegen ein bestehendes Dokument
// (Dokument-Picker + POST /rulesets/preview) — persistiert NICHTS bis "Speichern".
// Eigene Datei (Feature => eigene Datei, 500-LOC-Regel), damit AbRulesetAdmin.js klein bleibt.
// "Bearbeiten" legt bewusst KEIN PUT auf die bestehende Version, sondern immer eine neue
// (inaktive) Version an (version = max+1, serverseitig) — Statusmaschine der Regelsets.

import React, { useEffect, useState } from 'react';
import { ArrowLeft, Save, PlayCircle, AlertTriangle } from 'lucide-react';
import { Card, SectionTitle, Chip, btnGhost, btnPrimary, inputCls, money, qty, fmtDate } from './ui';
import { listDocuments, createRuleset, previewRuleset } from '../api';

const DEFAULT_RULES_TEXT = '{\n  "supplier_id": "",\n  "fields": {}\n}';
const PREVIEW_FIELDS = ['ebeln', 'ebelp', 'matnr', 'menge', 'netpr', 'eeind'];

const AbRulesetEditor = ({ user, isDarkMode, t, mode, ruleset, onClose, onSaved, showSuccess, showError }) => {
  const [supplierId, setSupplierId] = useState(ruleset?.supplierId || '');
  const [supplierName, setSupplierName] = useState(ruleset?.supplierName || '');
  const [rulesText, setRulesText] = useState(() => {
    if (!ruleset?.rulesJson) return DEFAULT_RULES_TEXT;
    return typeof ruleset.rulesJson === 'string' ? ruleset.rulesJson : JSON.stringify(ruleset.rulesJson, null, 2);
  });
  const [jsonError, setJsonError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [pickedDoc, setPickedDoc] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    let alive = true;
    listDocuments(user, { pageSize: 20 })
      .then((res) => { if (alive) setDocs(Array.isArray(res?.items) ? res.items : []); })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setDocsLoading(false); });
    return () => { alive = false; };
  }, [user]);

  const parseJson = () => {
    try {
      const obj = JSON.parse(rulesText);
      setJsonError(null);
      return obj;
    } catch (e) {
      setJsonError(e.message);
      return null;
    }
  };

  const runPreview = async () => {
    const obj = parseJson();
    if (!obj) return;
    if (!pickedDoc) { showError?.(t('ab.rulesetPreviewDocRequired')); return; }
    setPreviewBusy(true);
    setPreviewError(null);
    setPreviewResult(null);
    try {
      const res = await previewRuleset(user, { rulesJson: obj, documentId: Number(pickedDoc) });
      setPreviewResult(res);
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewBusy(false);
    }
  };

  const save = async () => {
    const obj = parseJson();
    if (!obj) return;
    if (!supplierId.trim() || !supplierName.trim()) { showError?.(t('ab.rulesetRequiredFields')); return; }
    setSaving(true);
    try {
      await createRuleset(user, { supplierId: supplierId.trim(), supplierName: supplierName.trim(), rulesJson: obj });
      showSuccess?.(t('ab.rulesetSavedMsg'));
      onSaved?.();
    } catch (e) {
      showError?.(e.message);
    } finally {
      setSaving(false);
    }
  };

  const textMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onClose} className={btnGhost(isDarkMode)}>
          <span className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />{t('common.back')}</span>
        </button>
        <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white/85' : 'text-slate-800'}`}>
          {mode === 'new' ? t('ab.rulesetNewVersion') : t('ab.rulesetEdit')}
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* LINKS: Stammdaten + JSON */}
        <Card isDarkMode={isDarkMode}>
          <SectionTitle isDarkMode={isDarkMode}>{t('ab.rulesetJsonLabel')}</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className={`text-[11px] uppercase tracking-wide font-semibold ${textMuted}`}>{t('ab.rulesetSupplierIdLabel')}</label>
              <input value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={`${inputCls(isDarkMode)} w-full mt-1`} />
            </div>
            <div>
              <label className={`text-[11px] uppercase tracking-wide font-semibold ${textMuted}`}>{t('ab.rulesetSupplierNameLabel')}</label>
              <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className={`${inputCls(isDarkMode)} w-full mt-1`} />
            </div>
          </div>
          <textarea
            value={rulesText}
            onChange={(e) => { setRulesText(e.target.value); setJsonError(null); }}
            onBlur={parseJson}
            rows={18}
            spellCheck={false}
            className={`${inputCls(isDarkMode)} w-full font-mono text-xs resize-y`}
          />
          {jsonError && (
            <p className={`text-xs mt-2 flex items-center gap-1.5 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />{t('ab.rulesetJsonInvalid', { error: jsonError })}
            </p>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={onClose} className={btnGhost(isDarkMode)} disabled={saving}>{t('common.cancel')}</button>
            <button onClick={save} className={btnPrimary(isDarkMode)} disabled={saving}>
              <span className="flex items-center gap-2">
                {saving ? t('common.saving') : <><Save className="w-4 h-4" />{t('common.save')}</>}
              </span>
            </button>
          </div>
        </Card>

        {/* RECHTS: Vorschau gegen ein bestehendes Dokument */}
        <Card isDarkMode={isDarkMode}>
          <SectionTitle isDarkMode={isDarkMode}>{t('ab.rulesetPreviewTitle')}</SectionTitle>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select value={pickedDoc} onChange={(e) => setPickedDoc(e.target.value)}
              className={`${inputCls(isDarkMode)} flex-1 min-w-[180px]`} disabled={docsLoading}>
              <option value="">{t('ab.rulesetPreviewDocPlaceholder')}</option>
              {docs.map((d) => <option key={d.id} value={d.id}>{d.fileName}</option>)}
            </select>
            <button onClick={runPreview} disabled={previewBusy || docsLoading || !pickedDoc} className={btnPrimary(isDarkMode)}>
              <span className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4" />{previewBusy ? t('ab.rulesetPreviewRunning') : t('ab.rulesetPreviewButton')}
              </span>
            </button>
          </div>

          {!previewBusy && !previewResult && !previewError && (
            <p className={`text-sm py-4 text-center ${textMuted}`}>{t('ab.rulesetPreviewEmptyHint')}</p>
          )}
          {previewError && (
            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('ab.rulesetPreviewError')}: {previewError}</p>
          )}
          {previewResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Chip tone={previewResult.matched ? 'green' : 'slate'} isDarkMode={isDarkMode}>
                  {previewResult.matched ? t('common.yes') : t('common.no')}
                </Chip>
                <span className={`text-sm ${textMuted}`}>{t('ab.rulesetPreviewResult', { lineCount: previewResult.lineCount ?? 0 })}</span>
              </div>
              {Array.isArray(previewResult.lines) && previewResult.lines.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
                        {PREVIEW_FIELDS.map((f) => (
                          <th key={f} className={`text-left px-2 py-1.5 uppercase tracking-wide font-semibold ${textMuted}`}>{t(`ab.field.${f}`)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewResult.lines.map((l, i) => (
                        <tr key={i} className={isDarkMode ? 'border-b border-white/5' : 'border-b border-slate-100'}>
                          <td className="px-2 py-1.5 font-mono">{l.ebeln || '—'}</td>
                          <td className="px-2 py-1.5 font-mono">{l.ebelp || '—'}</td>
                          <td className="px-2 py-1.5">{l.matnr || '—'}</td>
                          <td className="px-2 py-1.5 tabular-nums">{qty(l.menge)}</td>
                          <td className="px-2 py-1.5 tabular-nums">{money(l.netpr, l.waers)}</td>
                          <td className="px-2 py-1.5">{fmtDate(l.eeind)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AbRulesetEditor;
